import { SyntheticEvent, useEffect, useState } from 'react';

import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import { AutocompleteRenderValueGetItemProps, Box, Chip, IconButton, Stack } from '@mui/material';

import { StoreImportModal } from '../modal/store/StoreImportModal';
import { CissAutoComplete, CissAutoCompleteProps } from './CissAutoComplete';
import { TipoLojaAutoComplete, TipoLojaProps } from './TipoLojaAutoComplete';

import { useApplicationContext } from 'context/ApplicationContext';
import { usePermissionsContext } from 'context/PermissionsContext';
import { FilterValuesProps } from 'hooks/useFilter';
import { useLocalStorage } from 'hooks/useLocalStorage';

export interface StoreOptionsProps {
    idLoja: number;
    cdLoja: string;
    dsNomeFantasia: string;
}

export type StoreAutocompleteProps = CissAutoCompleteProps & {
    defaultStore?: boolean;
    importStores?: boolean;
    fgTipoLoja?: boolean;
};

const TIPO_LOJA = {
    PROPRIA: 1,
    FRANQUIA: 2,
    LOJINHA: 3,
    INSTITUCIONAL: 4,
    INSTITUCIONAL_CLIENTE_FINAL: 5,
    PERMUTA: 6,
    INSTITUCIONAL_S_MULTI: 7,
    GRUPO_FIT: 8,
} as const;

const TIPO_LOJA_PADRAO = [TIPO_LOJA.FRANQUIA, TIPO_LOJA.PROPRIA, TIPO_LOJA.LOJINHA, TIPO_LOJA.GRUPO_FIT];

const createChangeEvent = (): SyntheticEvent => new Event('change', { bubbles: true, cancelable: true }) as unknown as SyntheticEvent;

export function StoreAutoComplete({
    sx,
    name,
    size,
    value,
    error,
    options,
    selectedStringTemplate = '{cdLoja} - {dsNomeFantasia}',
    optionStringTemplate = '<strong>{cdLoja}</strong> - {dsNomeFantasia}',
    label = 'Loja',
    multiple = false,
    disabled = false,
    defaultStore = true,
    importStores = true,
    fgTipoLoja = false,
    onChange = () => {},
    requestOptions = {
        url: '/gestao/loja',
        sort: [{ property: 'dsNomeFantasia', direction: 'ASC' }],
        columns: 'idLoja,cdLoja,dsNomeFantasia,dsRazaoSocial',
        limit: 50,
    },
}: StoreAutocompleteProps) {
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [showHiddenTags, setShowHiddenTags] = useState(false);
    const [stores, setStores] = useState<StoreOptionsProps[]>([]);
    const { formattedStore, store } = useApplicationContext();
    const { hasPermission } = usePermissionsContext();
    const showTipoLoja = Boolean(hasPermission(['COMPRA_PEDIDO_ACESSA_LOJA_INSTITUCIONAL']) && fgTipoLoja);
    const { value: storageTipoLojaStr, setValue: setStorageTipoLoja } = useLocalStorage<string>('tipo-loja-store-auto-complete');
    const [tipoLoja, setTipoLoja] = useState<TipoLojaProps | null>(storageTipoLojaStr ? JSON.parse(storageTipoLojaStr) : null);

    /**
     * Define o novo tipo de loja e salva no storage a última seleção do usuário.
     */
    const handlerTipoLojaChange = (_: SyntheticEvent, newValue: TipoLojaProps | null) => {
        setTipoLoja(newValue);

        setStorageTipoLoja(newValue ? JSON.stringify(newValue) : null);

        onChange(createChangeEvent(), multiple ? [] : null);
    };

    /**
     * Define a loja padrão selecionada caso:
     * - tenha flag de defaultStore = true;
     * - mostra combo de tipo de loja, tem tipo de loja no storage e seu valor é o mesmo que o tipo de loja da loja logada (sim, confuso rs);
     * - mostra combo de tipo de loja, mas não tem tipo de loja no storage (define o tipo de loja da loja logada);
     * - não mostra combo de tipo de loja e a loja logada tem o tipo de loja padrão.
     */
    const applyDefaultStore = () => {
        if (!defaultStore || !store || stores.length) return;

        let applyDefaultStore = false;

        if (showTipoLoja && tipoLoja && tipoLoja.idTipoLoja === store.tipoLoja.idTipoLoja) {
            applyDefaultStore = true;
        } else if (showTipoLoja && !tipoLoja) {
            setTipoLoja(store.tipoLoja);
            setStorageTipoLoja(JSON.stringify(store.tipoLoja));

            applyDefaultStore = true;
        } else if (!showTipoLoja && TIPO_LOJA_PADRAO.includes(store?.tipoLoja.idTipoLoja as any)) {
            applyDefaultStore = true;
        }

        if (applyDefaultStore) {
            onChange(createChangeEvent(), multiple ? [formattedStore] : formattedStore);
        }
    };

    useEffect(applyDefaultStore, [store]);

    /**
     * Adiciona as lojas importadas ao estado local e dispara o evento onChange.
     */
    const setImportedStores = (lojas: StoreOptionsProps[]) => {
        const newLojas = lojas.filter((loja) => !stores.some((currentStore: StoreOptionsProps) => currentStore.cdLoja === loja.cdLoja));
        const newStores = [...stores, ...newLojas];

        setStores(newStores);

        onChange(createChangeEvent(), newStores);
    };

    /**
     * Renderiza as tags de forma limitada, mostrando apenas a primeira loja e um botão para mostrar as demais.
     */
    const renderLimitedTags = (tags: StoreOptionsProps[], getTagProps: AutocompleteRenderValueGetItemProps<boolean>) => {
        if (showHiddenTags) {
            return (
                <>
                    {tags.map((tag, index) => (
                        <Chip label={tag.dsNomeFantasia} {...getTagProps({ index })} key={`${tag.cdLoja}-${tag.idLoja}-${index}`} />
                    ))}

                    {tags.length > 1 && <Chip label={'▲ Ocultar Lojas'} onClick={() => setShowHiddenTags(!showHiddenTags)} color="primary" />}
                </>
            );
        }

        return (
            <>
                {tags.slice(0, 1).map((tag, index) => (
                    <Chip label={tag.dsNomeFantasia} {...getTagProps({ index })} key={`${tag.cdLoja}-${tag.idLoja}-1-${index}`} />
                ))}

                {tags.length > 1 && <Chip label={`+${tags.length - 1}`} onClick={() => setShowHiddenTags(true)} />}
            </>
        );
    };

    if (requestOptions && fgTipoLoja) {
        const newFilter: FilterValuesProps[] = requestOptions.filter ? requestOptions.filter.filter((item) => item.property !== 'idTipoLoja') : [];

        if (!showTipoLoja) {
            newFilter.push({
                property: 'idTipoLoja',
                operator: 'in',
                value: TIPO_LOJA_PADRAO,
            });
        } else if (tipoLoja) {
            newFilter.push({
                property: 'idTipoLoja',
                operator: 'in',
                value: [tipoLoja.idTipoLoja],
            });
        }

        requestOptions.filter = newFilter;
    }

    return (
        <Stack direction={{ xs: 'column', lg: 'row' }} sx={{ ...sx, gap: 2 }}>
            <StoreImportModal open={importModalOpen} setOpen={setImportModalOpen} onImportSuccess={setImportedStores} />

            {showTipoLoja && (
                <TipoLojaAutoComplete
                    size={size}
                    label="Tipo de Loja"
                    value={tipoLoja}
                    onChange={handlerTipoLojaChange}
                    sx={{
                        flex: { xs: 1, lg: 0 },
                        minWidth: { xs: '100%', lg: 200 },
                    }}
                />
            )}

            <Box sx={{ display: 'flex', flex: 1 }}>
                <CissAutoComplete
                    size={size}
                    name={name}
                    label={label}
                    value={value}
                    error={error}
                    objectId="cdLoja"
                    sx={{ flex: 1 }}
                    options={options}
                    onChange={onChange}
                    multiple={multiple}
                    disabled={disabled}
                    requestOptions={requestOptions}
                    optionStringTemplate={optionStringTemplate}
                    selectedStringTemplate={selectedStringTemplate}
                    renderValue={multiple ? renderLimitedTags : undefined}
                    isOptionEqualToValue={(option: StoreOptionsProps, value: StoreOptionsProps) =>
                        option?.idLoja === value?.idLoja && option?.cdLoja === value?.cdLoja
                    }
                />

                {multiple && importStores && (
                    <IconButton onClick={() => setImportModalOpen(true)} sx={{ width: 50 }} color="primary">
                        <UploadFileOutlinedIcon />
                    </IconButton>
                )}
            </Box>
        </Stack>
    );
}
